import { useTranslation } from "@coworker/locales";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { ViewerFragmentCommonProps } from "../ViewerFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import {
  MediaOrderedDetails,
  ItemNoAndType,
} from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { useMediasAndArticleCount } from "@coworker/app/src/components/RoomSettings/hooks/useMediasAndArticleCount";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import { MediaGroupedListView } from "@coworker/apprestructured/src/shared/components/MediaGroupedListView/MediaGroupedListView";
import { groupMediasByType } from "@coworker/apprestructured/src/task-management/helpers/media.helper";
import { MediasAndArticleCountResponse } from "@coworker/app/src/components/RoomSettings/types/media";
import LoadingViewerFragment from "../LoadingViewerFragment/LoadingViewerFragment";
import ModalColumn from "@coworker/apprestructured/src/task-management/components/ModalColumn/ModalColumn";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import styles from "./MediaLocationsViewerFragment.module.css";

export type MediaLocationsViewerFragmentConfigProps = {
  field: string;
  title: string;
};

type MediaLocationsViewerFragmentProps = {
  configProps: MediaLocationsViewerFragmentConfigProps;
  commonProps: ViewerFragmentCommonProps;
};

const MediaLocationsViewerFragment = ({
  configProps,
  commonProps,
}: MediaLocationsViewerFragmentProps) => {
  const { field, title } = configProps;
  const { task } = commonProps;
  const { t } = useTranslation();
  const navigate = useFixaNavigate();
  const storeId = useStoreId();

  const taskIsLoading = isLoading(task);

  const mediasOrderedDetails = taskIsLoading
    ? null
    : (getByPath(task, field) as MediaOrderedDetails[] | null);

  const itemNoAndType = taskIsLoading
    ? null
    : (getByPath(task, "details.item") as ItemNoAndType | null);

  const { data: mediasAndArticleCount, isLoading: isFetchingMedias } =
    useMediasAndArticleCount(itemNoAndType?.no, storeId);

  if (taskIsLoading || isFetchingMedias) {
    return <LoadingViewerFragment />;
  }

  if (!itemNoAndType) {
    return null;
  }

  if (
    !Array.isArray(mediasOrderedDetails) ||
    mediasOrderedDetails.length === 0
  ) {
    // ponytail: task can't be created with empty medias, so empty here means deleted
    return (
      <ModalColumn gap="small">
        <FixaText textStyle="Heading.S" tagName="h2">
          {t(title)}
        </FixaText>
        <FixaInlineMessage
          variant="negative"
          title={t("mediaDeletedString")}
          body={t("mediaDeletedInTaskDescriptionString")}
        />
      </ModalColumn>
    );
  }

  const matchedMedias: MediasAndArticleCountResponse[] =
    mediasAndArticleCount?.filter((media) =>
      mediasOrderedDetails.some(
        (m) => m.id === media.mediaId && m.article_id === media.articleId
      )
    ) ?? [];

  if (matchedMedias.length === 0) {
    return null;
  }

  const groupedMedias = groupMediasByType(matchedMedias);

  const handleMediaClick = (media: MediasAndArticleCountResponse) => {
    navigate(`/mediamaintenance/tabs/${media.mediaId}`);
  };

  return (
    <ModalColumn gap="small">
      <FixaText textStyle="Heading.S" tagName="h2">
        {t(title)}
      </FixaText>
      <MediaGroupedListView
        groupedMediasByType={groupedMedias}
        showChevron={true}
        showQuantityLabel={true}
        onItemClick={handleMediaClick}
        itemClassName={styles["mediaItem"]}
        headerClassName={styles["mediaTypeHeader"]}
        additionalProps={{ inset: true }}
      />
    </ModalColumn>
  );
};

export default MediaLocationsViewerFragment;
